<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import CustomizerApi from '@consumer/api/CustomizerApi'
import type GiftTag from '@consumer/types/GiftTag'
import type { MultiplaceGift } from '@consumer/types/serializers'
import type GiftTemplate from '@consumer/types/GiftTemplate'
import type GiftSeries from '@consumer/types/serializers/GiftSeries'
import { uniqBy } from 'lodash-es'
import TagsChooser, { type TagSelectOption } from './stepTags/TagsChooser.vue'

withDefaults(defineProps<{
  gift: GiftTemplate
  multiplaceGift?: MultiplaceGift
  editedGiftSeries?: GiftSeries | null
  giftSeries?: GiftSeries | null
}>(), {  })

const tagToSelectOption = (tag: GiftTag): TagSelectOption => ({
  ...tag,
  value: tag.id,
  label: tag.tag,
})
const initialGiftTags = computed(() => {
  const tags = __props.multiplaceGift?.giftTags ?? __props.gift.giftTags ?? []
  return tags.map(tagToSelectOption)
})

const selectedInterestedTags = _ref<TagSelectOption[]>(initialGiftTags.value.filter(
  tag => tag.tagType === 'interest') ?? [])

const selectedOccasionTags = _ref<TagSelectOption[]>(initialGiftTags.value.filter(
  tag => tag.tagType === 'occasion') ?? [])

const selectedRecipientTags = _ref<TagSelectOption[]>(initialGiftTags.value.filter(
  tag => tag.tagType === 'recipient') ?? [])

const selectedCategoryTags = _ref<TagSelectOption[]>(initialGiftTags.value.filter(
  tag => tag.tagType === 'category') ?? [])

if (__props.giftSeries?.giftTags) {
  selectedInterestedTags.value.splice(0, selectedInterestedTags.value.length,
    ...__props.giftSeries.giftTags.filter((tag: GiftTag) => tag.tagType === 'interest').map(tagToSelectOption))
  selectedOccasionTags.value.splice(0, selectedOccasionTags.value.length,
    ...__props.giftSeries.giftTags.filter((tag: GiftTag) => tag.tagType === 'occasion').map(tagToSelectOption))
  selectedRecipientTags.value.splice(0, selectedRecipientTags.value.length,
    ...__props.giftSeries.giftTags.filter((tag: GiftTag) => tag.tagType === 'recipient').map(tagToSelectOption))
  selectedCategoryTags.value.splice(0, selectedCategoryTags.value.length,
    ...__props.giftSeries.giftTags.filter((tag: GiftTag) => tag.tagType === 'category').map(tagToSelectOption))
}

let giftTags = _ref<TagSelectOption[]>(initialGiftTags)
let popularTags = _ref<Record<GiftTag['tagType'], GiftTag[]>>()
let giftSeriesOptions = _ref<GiftSeries[]>([])

onMounted(async () => {
  giftTags.value = uniqBy([...giftTags.value, ...(await CustomizerApi.giftTags())], 'id')
  popularTags.value = await CustomizerApi.popularTags() as Record<GiftTag['tagType'], GiftTag[]>
  giftSeriesOptions.value = [{ id: null, name: 'None' }, ...(await CustomizerApi.giftSeries())]
})

const allInterestTags = _computed(() => giftTags.value.filter((tag: GiftTag) => tag.tagType === 'interest'))
const allOccasionTags = _computed(() => giftTags.value.filter((tag: GiftTag) => tag.tagType === 'occasion'))
const allRecipientTags = _computed(() => giftTags.value.filter((tag: GiftTag) => tag.tagType === 'recipient'))
const allCategoryTags = _computed(() => giftTags.value.filter((tag: GiftTag) => tag.tagType === 'category'))

watchEffect(() => {
  const tags = selectedInterestedTags.value
    .concat(selectedOccasionTags.value)
    .concat(selectedRecipientTags.value)
    .concat(selectedCategoryTags.value)

  if (__props.multiplaceGift) {
    __props.multiplaceGift.giftTags = tags
  }
  else {
    __props.gift.giftTags = tags
  }
})

const selectedGiftSeries = ref<GiftSeries | null>(__props.multiplaceGift?.giftSeries || null)

watchEffect(() => {
  if (__props.multiplaceGift) {
    __props.multiplaceGift.giftSeries = selectedGiftSeries.value || undefined
  }
})

</script>
<template>
  <div class="flex flex-col gap-4" data-qa="customize-tags">
    <div class="flex flex-col gap-1">
      <div
        class="font-semibold"
        data-qa="tags-header"
      >
        List interests that the recipient of this gift would have
      </div>
      <TagsChooser
        v-model="selectedInterestedTags"
        :allTags="allInterestTags"
        :popular="popularTags?.interest.map(tagToSelectOption)"
        name="interests"
        tagType="interest"
      />
    </div>
    <div class="flex flex-col gap-1">
      <div
        class="font-semibold"
        data-testid="occasion-header"
      >
        Most relevant occasions or holidays?
      </div>
      <TagsChooser
        v-model="selectedOccasionTags"
        :allTags="allOccasionTags"
        :popular="popularTags?.occasion.map(tagToSelectOption)"
        name="occasions"
        tagType="occasion"
      />
    </div>
    <div class="flex flex-col gap-1">
      <div class="font-semibold" data-testid="recipient-header">Most relevant recipient demographics/persona?</div>
      <TagsChooser
        v-model="selectedRecipientTags"
        :allTags="allRecipientTags"
        :popular="popularTags?.recipient.map(tagToSelectOption)"
        name="recipients"
        tagType="recipient"
      />
    </div>
    <div class="flex flex-col gap-1">
      <div class="font-semibold" data-testid="category-header">Categories</div>
      <TagsChooser
        v-model="selectedCategoryTags"
        :allTags="allCategoryTags"
        :popular="popularTags?.category?.map(tagToSelectOption)"
        name="categories"
        tagType="category"
      />
    </div>

    <div v-if="multiplaceGift && !editedGiftSeries && !giftSeries" class="flex flex-col gap-1">
      <div class="font-semibold" data-testid="gift-series-header">Gift Series</div>
      <GSelectInput
        v-model="selectedGiftSeries"
        :options="giftSeriesOptions"
        valueProp="id"
        labelProp="name"
        name="gift-series"
        :wholeItem="true"
        class="bg-white"
      />
    </div>

    <div class="block lg:hidden" style="height: 200px;"/>
  </div>
</template>
